import { Download, FileSpreadsheet, ClipboardList, Share2 } from "lucide-react";

const steps = [
  {
    n: "01",
    icon: Download,
    title: "Bring your own records",
    desc: "Download your animal data from your existing provider — or email our support desk for a hand.",
  },
  {
    n: "02",
    icon: FileSpreadsheet,
    title: "Import to Arvo",
    desc: "Drop your CSV into Arvo. EID, LID and Management Tag — verified and ready to use on-farm.",
  },
  {
    n: "03",
    icon: ClipboardList,
    title: "Work in the yards",
    desc: "Scan, weigh, score, sample. EID reader or manual entry — records build live as you work.",
  },
  {
    n: "04",
    icon: Share2,
    title: "Export when you're done",
    desc: "Review your session and export the CSV yourself — share with your vet, livestock representative or office on your terms.",
  },
];

export default function HowItWorks() {
  return (
    <section
      id="how"
      data-testid="how-section"
      className="bg-[#1F3617] py-20 md:py-28 relative overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-[0.06] bg-[length:400px] bg-repeat pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'><circle cx='20' cy='20' r='1' fill='white'/></svg>\")",
        }}
        aria-hidden
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <span className="inline-block text-xs font-bold tracking-[0.2em] uppercase text-[#F2C76A]">
            How It Works
          </span>
          <h2
            data-testid="how-headline"
            className="mt-4 font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-white leading-tight"
          >
            You bring the data. We give you the tool.
          </h2>
          <p className="mt-5 text-lg text-white/80 leading-relaxed">
            Arvo is independent and provider-neutral. You stay in control of
            your records — we just make them easy to work with in the yard.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-3">
          {steps.map((s, idx) => {
            const Icon = s.icon;
            return (
              <div
                key={s.n}
                data-testid={`how-step-${s.n}`}
                className="relative bg-white/5 backdrop-blur-sm border border-white/15 rounded-md p-4 sm:p-7"
              >
                <div className="flex items-baseline justify-between">
                  <span className="font-display font-bold text-3xl sm:text-5xl text-[#F2C76A] leading-none">
                    {s.n}
                  </span>
                  <Icon className="w-5 h-5 sm:w-7 sm:h-7 text-white/70" strokeWidth={2} />
                </div>
                <h3 className="mt-4 sm:mt-6 font-display font-bold text-base sm:text-xl text-white">
                  {s.title}
                </h3>
                <p className="mt-2 sm:mt-3 text-xs sm:text-sm text-white/75 leading-relaxed">
                  {s.desc}
                </p>
                {idx < steps.length - 1 && (
                  <div
                    className="hidden lg:block absolute top-1/2 -right-3 w-6 h-px bg-white/20"
                    aria-hidden
                  />
                )}
              </div>
            );
          })}
        </div>

        <p
          data-testid="how-support-note"
          className="mt-10 text-sm text-white/70 max-w-2xl"
        >
          Need a hand getting started? Email our support desk at{" "}
          <a
            href="mailto:support@getarvo.co.nz"
            className="font-bold text-[#F2C76A] underline-offset-4 hover:underline"
          >
            support@getarvo.co.nz
          </a>{" "}
          and we'll get your data ready.
        </p>
      </div>
    </section>
  );
}
